import { EventType, MsgType, RoomEvent, RoomStateEvent } from 'matrix-js-sdk'
import type { MatrixClient, MatrixEvent, Room } from 'matrix-js-sdk'
import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import {
  MATRIX_PERSONAL_PINNED_ACCOUNT_TYPE,
  MATRIX_PINNED_BY_CONTENT_KEY,
  MATRIX_PIN_NOTICE_CONTENT_KEY,
} from '@/modules/chat-matrix-fast/constants/chat'
import { startMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { formatMatrixDisplayName } from '@/modules/chat-matrix-fast/services/matrixUserId'
import { ChatApiService } from '@/modules/chat-matrix-fast/services/chatApiService'

type PinnedByMap = Record<string, string>

interface PersonalPinnedAccount {
  pinned: string[]
  /** Shared pins скрытые только у меня (диалог, чужой пин) */
  hidden: string[]
}

function isPinnedEventsForbiddenError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }

  const matrixError = error as {
    httpStatus?: number
    statusCode?: number
    errcode?: string
    data?: { errcode?: string; error?: string }
    message?: string
  }
  const errcode = matrixError.errcode || matrixError.data?.errcode
  const message = String(matrixError.data?.error || matrixError.message || '')
  const status = matrixError.httpStatus ?? matrixError.statusCode

  if (status !== 403 && errcode !== 'M_FORBIDDEN') {
    return false
  }

  return message.includes('send_level')
    || message.includes('permission to post')
    || errcode === 'M_FORBIDDEN'
}

function canSendPinnedEventsState(room: Room | null, userId: string | null): boolean {
  if (!room?.currentState || !userId) {
    return false
  }

  return room.currentState.maySendStateEvent(EventType.RoomPinnedEvents, userId)
}

async function waitUntilCanPin(roomId: string, attempts = 20, delayMs = 400): Promise<boolean> {
  const matrixClient = await startMatrixClient()
  const myUserId = matrixClient.getUserId()
  if (!myUserId) {
    return false
  }

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const room = matrixClient.getRoom(roomId)
    if (canSendPinnedEventsState(room, myUserId)) {
      return true
    }
    await new Promise<void>(resolve => setTimeout(resolve, delayMs))
  }

  return canSendPinnedEventsState(matrixClient.getRoom(roomId), myUserId)
}

async function healPinnedEventsPermission(roomId: string): Promise<boolean> {
  try {
    const chat = await ChatApiService.resolveRoom(roomId)
    await ChatApiService.syncPowerLevels(chat.id)
  } catch {
    return false
  }

  return waitUntilCanPin(roomId, 40, 250)
}

function readPinnedIdsFromContent(content: Record<string, unknown> | undefined): string[] {
  const pinned = content?.pinned
  if (!Array.isArray(pinned)) {
    return []
  }

  return pinned.filter((id): id is string => typeof id === 'string' && Boolean(id))
}

function readPinnedByMap(content: Record<string, unknown> | undefined): PinnedByMap {
  const raw = content?.[MATRIX_PINNED_BY_CONTENT_KEY]
  if (!raw || typeof raw !== 'object') {
    return {}
  }

  const result: PinnedByMap = {}
  for (const [eventId, userId] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof userId === 'string' && userId) {
      result[eventId] = userId
    }
  }
  return result
}

function readRoomPinnedState(room: Room | null | undefined): {
  pinned: string[]
  pinnedBy: PinnedByMap
} {
  if (!room?.currentState) {
    return { pinned: [], pinnedBy: {} }
  }

  const event = room.currentState.getStateEvents(EventType.RoomPinnedEvents, '')
  const content = event?.getContent() as Record<string, unknown> | undefined
  const pinned = readPinnedIdsFromContent(content)
  const pinnedBy = readPinnedByMap(content)

  // Оставляем авторов только для актуальных pin'ов
  const filteredBy: PinnedByMap = {}
  for (const id of pinned) {
    if (pinnedBy[id]) {
      filteredBy[id] = pinnedBy[id]
    }
  }

  return { pinned, pinnedBy: filteredBy }
}

function readPersonalAccount(room: Room | null | undefined): PersonalPinnedAccount {
  if (!room) {
    return { pinned: [], hidden: [] }
  }

  const event = room.getAccountData(MATRIX_PERSONAL_PINNED_ACCOUNT_TYPE as never)
  const content = event?.getContent() as Record<string, unknown> | undefined
  const pinned = readPinnedIdsFromContent(content)
  const hiddenRaw = content?.hidden
  const hidden = Array.isArray(hiddenRaw)
    ? hiddenRaw.filter((id): id is string => typeof id === 'string' && Boolean(id))
    : []

  return { pinned, hidden }
}

function mergeUniqueIds(...lists: string[][]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const list of lists) {
    for (const id of list) {
      if (seen.has(id)) {
        continue
      }
      seen.add(id)
      result.push(id)
    }
  }
  return result
}

function getMergedPinnedEventIds(matrixClient: MatrixClient, roomId: string): string[] {
  const room = matrixClient.getRoom(roomId)
  const { pinned: roomPinned } = readRoomPinnedState(room)
  const personal = readPersonalAccount(room)
  const hidden = new Set(personal.hidden)
  const visibleShared = roomPinned.filter(id => !hidden.has(id))
  return mergeUniqueIds(visibleShared, personal.pinned)
}

export function subscribeMatrixPinnedEvents(
  roomId: string,
  onUpdate: (eventIds: string[]) => void,
): () => void {
  let matrixClient: MatrixClient | undefined
  let stopped = false

  const publish = () => {
    if (stopped || !matrixClient) {
      return
    }

    onUpdate(getMergedPinnedEventIds(matrixClient, roomId))
  }

  const onState = (event: MatrixEvent) => {
    if (event.getRoomId() !== roomId) {
      return
    }

    if (event.getType() !== EventType.RoomPinnedEvents) {
      return
    }

    publish()
  }

  const onAccountData = (event: MatrixEvent, room?: Room) => {
    if (room?.roomId !== roomId) {
      return
    }

    if (event.getType() !== MATRIX_PERSONAL_PINNED_ACCOUNT_TYPE) {
      return
    }

    publish()
  }

  void startMatrixClient().then(startedClient => {
    if (stopped) {
      return
    }

    matrixClient = startedClient
    matrixClient.on(RoomStateEvent.Events, onState)
    matrixClient.on(RoomEvent.AccountData, onAccountData)
    publish()
  })

  return () => {
    stopped = true
    matrixClient?.removeListener(RoomStateEvent.Events, onState)
    matrixClient?.removeListener(RoomEvent.AccountData, onAccountData)
  }
}

async function setRoomPinnedState(
  roomId: string,
  pinned: string[],
  pinnedBy: PinnedByMap,
): Promise<void> {
  const matrixClient = await startMatrixClient()
  const filteredBy: PinnedByMap = {}
  for (const id of pinned) {
    if (pinnedBy[id]) {
      filteredBy[id] = pinnedBy[id]
    }
  }

  const content = {
    pinned,
    [MATRIX_PINNED_BY_CONTENT_KEY]: filteredBy,
  } as never

  try {
    await matrixClient.sendStateEvent(
      roomId,
      EventType.RoomPinnedEvents,
      content,
      '',
    )
  } catch (error) {
    if (!isPinnedEventsForbiddenError(error)) {
      throw error
    }

    const healed = await healPinnedEventsPermission(roomId)
    if (!healed) {
      throw new Error(
        'Нет прав на закрепление в этой комнате. Права обновляются — попробуйте через несколько секунд.',
      )
    }

    await matrixClient.sendStateEvent(
      roomId,
      EventType.RoomPinnedEvents,
      content,
      '',
    )
  }
}

async function setPersonalAccount(
  roomId: string,
  account: PersonalPinnedAccount,
): Promise<void> {
  const matrixClient = await startMatrixClient()
  // Убираем hidden для event_id, которых уже нет в room pins — чистим при записи
  await matrixClient.setRoomAccountData(
    roomId,
    MATRIX_PERSONAL_PINNED_ACCOUNT_TYPE as never,
    {
      pinned: account.pinned,
      hidden: account.hidden,
    } as never,
  )
}

function pruneHiddenAgainstRoomPins(
  hidden: string[],
  roomPinned: string[],
): string[] {
  const roomSet = new Set(roomPinned)
  return hidden.filter(id => roomSet.has(id))
}

export async function pinMatrixMessage(roomId: string, eventId: string): Promise<void> {
  const matrixClient = await startMatrixClient()
  const myUserId = matrixClient.getUserId()
  const room = matrixClient.getRoom(roomId)
  const { pinned, pinnedBy } = readRoomPinnedState(room)
  if (pinned.includes(eventId)) {
    return
  }

  if (!canSendPinnedEventsState(room, myUserId)) {
    const healed = await healPinnedEventsPermission(roomId)
    if (!healed) {
      throw new Error(
        'Нет прав на закрепление в этой комнате. Обновите страницу и попробуйте снова.',
      )
    }
  }

  const nextBy = { ...pinnedBy }
  if (myUserId) {
    nextBy[eventId] = myUserId
  }

  await setRoomPinnedState(roomId, [...pinned, eventId], nextBy)

  const personal = readPersonalAccount(matrixClient.getRoom(roomId))
  if (personal.hidden.includes(eventId)) {
    await setPersonalAccount(roomId, {
      pinned: personal.pinned,
      hidden: personal.hidden.filter(id => id !== eventId),
    })
  }
}

export async function pinMatrixMessagePersonal(roomId: string, eventId: string): Promise<void> {
  const matrixClient = await startMatrixClient()
  const room = matrixClient.getRoom(roomId)
  const personal = readPersonalAccount(room)
  if (personal.pinned.includes(eventId)) {
    return
  }

  await setPersonalAccount(roomId, {
    pinned: [...personal.pinned, eventId],
    hidden: personal.hidden.filter(id => id !== eventId),
  })
}

/**
 * Unpin:
 * - group: всегда у всех (room state)
 * - dialog: если пин ставил я → у всех; если чужой shared → только у меня (hidden)
 */
export async function unpinMatrixMessage(
  roomId: string,
  eventId: string,
  options: { scope: 'group' | 'dialog' } = { scope: 'group' },
): Promise<void> {
  const matrixClient = await startMatrixClient()
  const myUserId = matrixClient.getUserId()
  const room = matrixClient.getRoom(roomId)
  const { pinned: roomPinned, pinnedBy } = readRoomPinnedState(room)
  const personal = readPersonalAccount(room)

  const inPersonal = personal.pinned.includes(eventId)
  const inRoom = roomPinned.includes(eventId)

  if (inPersonal) {
    await setPersonalAccount(roomId, {
      pinned: personal.pinned.filter(id => id !== eventId),
      hidden: pruneHiddenAgainstRoomPins(
        personal.hidden.filter(id => id !== eventId),
        roomPinned,
      ),
    })
  }

  if (!inRoom) {
    return
  }

  if (options.scope === 'group') {
    const nextPinned = roomPinned.filter(id => id !== eventId)
    const nextBy = { ...pinnedBy }
    delete nextBy[eventId]
    await setRoomPinnedState(roomId, nextPinned, nextBy)
    return
  }

  // dialog
  const authorId = pinnedBy[eventId]
  const iAmAuthor = Boolean(myUserId && authorId && authorId === myUserId)

  if (iAmAuthor) {
    const nextPinned = roomPinned.filter(id => id !== eventId)
    const nextBy = { ...pinnedBy }
    delete nextBy[eventId]
    await setRoomPinnedState(roomId, nextPinned, nextBy)

    const latestPersonal = readPersonalAccount(matrixClient.getRoom(roomId))
    await setPersonalAccount(roomId, {
      pinned: latestPersonal.pinned.filter(id => id !== eventId),
      hidden: pruneHiddenAgainstRoomPins(
        latestPersonal.hidden.filter(id => id !== eventId),
        nextPinned,
      ),
    })
    return
  }

  // Чужой shared-pin (или без автора) — скрыть только у себя
  if (!personal.hidden.includes(eventId)) {
    const latestPersonal = readPersonalAccount(room)
    await setPersonalAccount(roomId, {
      pinned: latestPersonal.pinned.filter(id => id !== eventId),
      hidden: pruneHiddenAgainstRoomPins(
        [...latestPersonal.hidden, eventId],
        roomPinned,
      ),
    })
  }
}

export async function sendPinNotice(
  roomId: string,
  options: {
    eventId: string
    preview: string
    actorName: string
  },
): Promise<void> {
  const matrixClient = await startMatrixClient()
  const preview = options.preview.trim() || 'сообщение'
  const body = `${options.actorName} закрепил(а) «${preview}»`

  await matrixClient.sendEvent(roomId, EventType.RoomMessage, {
    msgtype: MsgType.Notice,
    body,
    [MATRIX_PIN_NOTICE_CONTENT_KEY]: {
      event_id: options.eventId,
    },
  } as never)
}

function hashToNumber(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(i)
  }

  return Math.abs(hash)
}

function eventToPinnedPreview(event: MatrixEvent): IMessage | null {
  if (event.isRedacted()) {
    return null
  }

  const content = event.getContent() as Record<string, unknown>
  const newContent = content['m.new_content'] as { body?: string } | undefined
  const body = String(newContent?.body || content.body || '').trim()
  const senderId = event.getSender() ?? ''
  const eventId = event.getId()
  if (!eventId) {
    return null
  }

  const displayName = event.sender?.name
    || (senderId ? formatMatrixDisplayName(senderId) : 'Сообщение')

  return {
    id: hashToNumber(eventId),
    matrix_event_id: eventId,
    content: body || (content.url ? 'Вложение' : ''),
    author: senderId,
    author_id: senderId,
    author_name: displayName,
    created_by: {
      id: senderId,
      full_name: displayName,
      user_name: senderId,
      username: senderId,
    },
    attachments: [],
    created_at: new Date(event.getTs()).toISOString(),
  }
}

/** Resolve pinned event_ids to message previews (local → timeline → fetch). */
export async function resolvePinnedMessages(
  roomId: string,
  eventIds: string[],
  localMessages: Array<Pick<IMessage, 'id' | 'matrix_event_id' | 'content' | 'author_name' | 'created_by' | 'attachments' | 'created_at' | 'author' | 'author_id'>> = [],
): Promise<IMessage[]> {
  if (!eventIds.length) {
    return []
  }

  const byEventId = new Map<string, IMessage>()
  for (const message of localMessages) {
    if (message.matrix_event_id) {
      byEventId.set(message.matrix_event_id, message as IMessage)
    }
  }

  const matrixClient = await startMatrixClient()
  const room = matrixClient.getRoom(roomId)
  const result: IMessage[] = []

  for (const eventId of eventIds) {
    const fromLocal = byEventId.get(eventId)
    if (fromLocal) {
      result.push(fromLocal)
      continue
    }

    const fromTimeline = room?.findEventById(eventId)
    if (fromTimeline) {
      const preview = eventToPinnedPreview(fromTimeline)
      if (preview) {
        result.push(preview)
      }
      continue
    }

    try {
      const raw = await matrixClient.fetchRoomEvent(roomId, eventId)
      const mapped = matrixClient.getEventMapper()(raw)
      const preview = eventToPinnedPreview(mapped)
      if (preview) {
        result.push(preview)
      }
    } catch {
      // deleted / no access
    }
  }

  return result
}
